/**
 * The Analytics page's OPTIONAL narrowing dimensions — the field keys, the two status
 * catalogues, and the transfer collapse/expand pair.
 *
 * ## Why these live in their own module
 *
 * They had one consumer (`AnalyticsFiltersHeader`, which builds the drawer's `columns`
 * and the redux adapter from them) until `AnalyticsAppliedFilters` needed the same
 * field list and the same status labels to draw a chip per applied dimension. Two
 * copies of "which keys are filters, and what is a status called" is exactly the drift
 * §36 is about — and the alternative, importing the header component for two constants,
 * would drag the whole MUI date-picker chain into a module that renders chips.
 *
 * ⚠️ WHAT IS DELIBERATELY ABSENT, because both consumers depend on the omission:
 * `dateRange` and the per-panel data type are NOT here. They are not optional narrowing
 * — a range is always set and every panel always counts something — so they get no
 * drawer field and no chip. See `AnalyticsAppliedFilters` for why an always-populated
 * chip row would be furniture rather than a signal.
 */
import { getScanStatus, palette, type ScanStatusKey } from '@repo/ui'

import { CERTIFICATE_STATUSES, CERT_TRANSFER_STATUSES } from 'constants/statuses'

/**
 * The six drawer-editable dimension columns' `field`s, which are ALSO the redux keys
 * they map to — PLUS `area`, which has no drawer column at all (its editing surface is
 * the map's draw control, not the drawer; see `AnalyticsAppliedFilters`).
 *
 * The translation between the drawer's `{field, operator, value}` shape and
 * `state.analytics.filters` is deliberately the DUMBEST one available: a column's
 * `field` IS its redux key, so both directions are a rename-free lookup and there is no
 * mapping table to drift. See `AnalyticsFiltersHeader` for the full adapter note.
 *
 * ⚠️ `area` is included here ONLY so `AnalyticsAppliedFilters` can share the same
 * `AnalyticsFilterField` union and the same exhaustive `Record<AnalyticsFilterField, …>`
 * labels for it. `AnalyticsFiltersHeader` builds its drawer `columns` by referencing each
 * `FILTER_FIELDS.*` key EXPLICITLY (never `Object.values(FILTER_FIELDS)`), so adding this
 * key does not — and must not — grow the drawer by one field. If that ever changes to a
 * generic iteration, `area` needs an explicit exclusion.
 */
export const FILTER_FIELDS = {
  status: 'certificateStatuses',
  /**
   * The SCAN's own outcome — Verified / Invalid / Opened (VERA-260).
   *
   * A SECOND status axis, not a widening of the first, and the two must never be merged
   * into one control. A CERTIFICATE is Created / Active / Lost / Stolen; a SCAN is
   * Verified / Invalid / Opened. They are orthogonal — an invalid scan of an active
   * certificate is the single most interesting row on this page, and it satisfies
   * "Active" and "Invalid" at once. In one multi-select, selecting both has no
   * defensible meaning: as an AND it is the useful query, as an OR it is a union of two
   * unrelated sets, and the control cannot say which it did.
   */
  scanStatus: 'scanStatuses',
  owner: 'ownerIds',
  product: 'productIds',
  certificate: 'certificateIds',
  location: 'location',
  area: 'area',
} as const

export type AnalyticsFilterField = (typeof FILTER_FIELDS)[keyof typeof FILTER_FIELDS]

/**
 * One option per LABEL, not per status — the four in-flight transfer stages all read
 * "Transfer" (see `constants/statuses.ts`), so deriving 1:1 offers four identical chips
 * each matching a quarter of the transfers. Keeping the first occurrence gives one
 * "Transfer" chip; `expandStatuses` below turns it back into every value it stands for.
 *
 * The authored `backgroundColor`/`borderColor` ride along, not just the hue, so a
 * selected chip here is the same colour as that status everywhere else it is drawn.
 *
 * NOTE(§36): `CertificatesPage` derives a byte-identical `CERT_STATUS_FILTER_OPTIONS`
 * and its own `expandStatusFilter`, both file-local. That is a third copy, so the pair
 * belongs in `constants/statuses.ts` beside the map they read — deliberately not moved
 * here because this module is scoped to the analytics surface.
 */
export const CERT_STATUS_OPTIONS = Object.entries(CERTIFICATE_STATUSES)
  .map(([value, cfg]) => ({
    value,
    label: (cfg as any).label as string,
    color: (cfg as any).color as string,
    backgroundColor: (cfg as any).backgroundColor as string | undefined,
    borderColor: (cfg as any).borderColor as string | undefined,
  }))
  .filter((opt, i, all) => all.findIndex(o => o.label === opt.label) === i)

const TRANSFER_STATUSES = CERT_TRANSFER_STATUSES as readonly string[]

/** Chip selection -> the status values the API filters on. */
export const expandStatuses = (selected: string[]): string[] =>
  Array.from(
    new Set(selected.flatMap(v => (TRANSFER_STATUSES.includes(v) ? [...TRANSFER_STATUSES] : [v]))),
  )

/** Stored status values -> the chips that should read as selected. */
export const collapseStatuses = (stored: string[]): string[] => {
  const set = new Set(stored)
  const anyTransfer = stored.some(v => TRANSFER_STATUSES.includes(v))
  return CERT_STATUS_OPTIONS.map(o => o.value).filter(v =>
    TRANSFER_STATUSES.includes(v) ? anyTransfer : set.has(v),
  )
}

/* ────────────────────────────────────────────────────────────────────────────────────
   SCAN OUTCOME — the second status axis (VERA-260)
   ──────────────────────────────────────────────────────────────────────────────────── */

/**
 * The scan outcomes this page can filter by, in display order.
 *
 * ⚠️ THREE, not the seven in `@repo/ui`'s `SCAN_STATUS_FILTER_OPTIONS`. That list is the
 * full vocabulary `getScanStatus` can return, and it is right for the certificate page,
 * whose rows carry a whole scan. The analytics scan-map endpoint selects only
 * `invalid` / `tampered` / `metadata` per point: `connected` needs a Connect assurance
 * flag and the three `link_*` keys come from `public_verify_logs`, a table this endpoint
 * does not read at all. Offering them would put options on screen that can never match
 * anything — worse than an absent filter, because "no suspicious links" reads as a claim
 * about the DATA when the truth is that this surface cannot see that field.
 *
 * ⚠️ `tampered` is the VALUE and "Opened" is the LABEL. An open seal is a fact about the
 * seal, not a failed scan (`getScanStatus`'s own note). The API enum uses the same three
 * values (`ScanOutcomeStatus`), so nothing translates between the two.
 */
const SCAN_STATUS_KEYS = ['verified', 'invalid', 'tampered'] as const satisfies readonly ScanStatusKey[]

/**
 * The resolver's own verdict for each key, obtained by handing it the MINIMAL input that
 * produces that key — which is also a live assertion that these three inputs still
 * resolve the way this module assumes.
 *
 * Derived rather than transcribed, so the labels here cannot drift from the labels the
 * map pins, the scan list and the certificate page draw for the same outcome (§44: one
 * definition, every surface reads it). `{}` -> `verified` is the resolver's own
 * fallthrough; `{invalid: true}` and `{tampered: true}` hit its first two branches.
 *
 * ⚠️ This mirrors the SERVER's precedence deliberately. `buildScanOutcomeFilter`
 * (`apps/api/src/domain/types/scan-outcome-status.ts`) partitions the same three states
 * invalid-before-tampered-before-verified, because the same rows are ALSO resolved
 * client-side by `getScanStatus` when the map card colours its pins. A server predicate
 * that partitioned them differently would make the endpoint and the card disagree about
 * what a scan is, in a way a reader could not see.
 */
const scanStatusMeta = (key: (typeof SCAN_STATUS_KEYS)[number]) =>
  getScanStatus(
    key === 'invalid' ? { invalid: true } : key === 'tampered' ? { tampered: true } : {},
  )

/**
 * Scan outcomes in the shared `{ label, color, borderColor, backgroundColor }` status
 * shape — the same one `CERTIFICATE_STATUSES` uses, so `StatusChip` / `StatusPill` paint
 * these exactly as they paint every other status in the app.
 *
 * `color` is the TEXT/derivation hue, `backgroundColor` the pale fill, `borderColor` the
 * mid-tone edge — matching that map's 700 / 100 / 200 structure. `getScanStatus`'s `chip`
 * ramp already IS that structure (its own docblock says it reuses the certificate-status
 * three-tone family), so all three values are read straight off the resolver.
 *
 * ⚠️ THREE DELIBERATE DEVIATIONS from the resolver's ramp, all about this being a FILTER
 * chip rather than a table chip:
 *
 *  1. `invalid` gets `color: '#DC2626'` — the resolver's own alert red, the hue it gives
 *     the map pin — instead of its near-black chip text `#27272A`. `statusTone`
 *     (`StatusChip`) DERIVES the whole dark-mode chip from `color` (`lighten`/`alpha`),
 *     so a near-black `color` renders as grey in dark mode. Invalid and Opened both carry
 *     near-black text, and their light-mode distinction is the border — which dark mode
 *     also derives from `color`. Taken verbatim, the two outcomes would be pixel-identical
 *     pills in dark mode.
 *  2. `invalid` gets a pale red FILL where the resolver authors `transparent`. The reason
 *     for transparent there is row-level ranking (an invalid scan is evidence, and should
 *     not shout as loudly as a confirmed Stolen certificate in a long table). Here the
 *     fill is the SELECTED-state signal — `StatusPill` fills a selected chip with its
 *     tint and leaves an unselected one a muted outline — so a transparent fill would
 *     make a selected option look unselected.
 *  3. 🚨 `verified` is BRAND PURPLE, not the resolver's `chip` green. This one is a
 *     correction rather than a tuning, and it is worth stating why the green was never
 *     reachable: `getScanStatus` sets `showChip: false` for `verified` and its own comment
 *     on that ramp reads "Never rendered (showChip is false) — present so the type stays
 *     total." This filter was the ONLY consumer of it, so the one place in the app where a
 *     "Verified" token is drawn was painting it a colour no other surface uses, against a
 *     resolver that considers the value dead.
 *
 *     The visible symptom was the hover. `StatusPill` hovers an unselected chip with
 *     `alpha(option.color, 0.08)`, and `option.color` was the ramp's near-black green
 *     `#14532B` — measured in the browser as `rgba(20, 83, 43, 0.08)`, which composites
 *     over the drawer's white to `rgb(236, 241, 237)`. That is grey, and it was reported
 *     as grey. Dark mode was worse for the reason deviation 1 already names: `statusTone`
 *     derives everything from `color`, and `lighten('#14532B', 0.4)` is a muddy grey-green.
 *
 *     `meta.color` (`#7F48DF` light, `#8F68EA` dark) is what `getScanStatus` actually
 *     authors for verified — it is the hue the MAP PIN uses, the hue the map legend's
 *     swatch uses, and the app's brand purple. Taking it here makes the drawer chip, the
 *     toolbar chip, the map legend pill and the pin itself one colour for one status,
 *     which is the whole §44 point. The `brand[100]`/`brand[200]` fill and edge keep the
 *     700/100/200 structure every other status in this list follows.
 *
 * `tampered` is taken verbatim — its neutral zinc is authored, live (it DOES render a
 * chip), and deliberately not an alert.
 */
const BRAND = (palette as { brand?: Record<string, string> }).brand ?? {}

export const SCAN_STATUS_OPTIONS = SCAN_STATUS_KEYS.map(key => {
  const meta = scanStatusMeta(key)
  // The verified/invalid hue is `meta.color` — the resolver's own marker hue — for the
  // reasons in deviations 1 and 3. `tampered` keeps its authored chip text.
  const usesMarkerHue = key === 'invalid' || key === 'verified'
  return {
    value: key as string,
    label: meta.label,
    color: usesMarkerHue ? meta.color : meta.chip.text,
    backgroundColor:
      key === 'invalid'
        ? '#FEE2E2'
        : key === 'verified'
          ? BRAND['100'] ?? '#EEEBFC'
          : meta.chip.fill,
    borderColor: key === 'verified' ? BRAND['200'] ?? '#DFD9FB' : meta.chip.border,
  }
})

/**
 * The same three, as the `{ [value]: Status }` record `StatusChip` takes — for the
 * toolbar trigger, which displays the current selection as read-only chips.
 *
 * Built FROM `SCAN_STATUS_OPTIONS` rather than beside it, so the trigger's chips and the
 * popover's toggle pills cannot end up two different colours (§36). `glossaryId` rides
 * along from the resolver, so hovering a chip shows the term's own definition (§44) with
 * no extra `(i)` glyph.
 */
export const SCAN_STATUSES: Record<
  string,
  { label: string; color: string; borderColor?: string; backgroundColor?: string; glossaryId?: string }
> = Object.fromEntries(
  SCAN_STATUS_OPTIONS.map(opt => [
    opt.value,
    {
      label: opt.label,
      color: opt.color,
      borderColor: opt.borderColor,
      backgroundColor: opt.backgroundColor,
      glossaryId: scanStatusMeta(opt.value as (typeof SCAN_STATUS_KEYS)[number]).glossaryId,
    },
  ]),
)
